import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { GeoidService } from '../../../../global/components/forms/geoid-address/service/geoid.abstract.service';
import { MessageService } from '../../../../global/services/message/message.abstract.service';
import { Address } from '../../../../user/user.model';
import { FormFieldConfig, FormSectionConfig, PermitTypeConfig, displayUnitFor, groupFieldsBySections } from '../../../models/permit-type.model';
import { I18nString } from '../../../models/permit-category.model';
import { PermitV2Service } from '../../../services/permits.abstract.service';

/** One page of the Details sub-stepper: either a content group (from formSections) or the trailing Documents page. */
interface SubStep {
    title: I18nString | null;
    fields: FormFieldConfig[];
    isDocuments: boolean;
}

/** Mirrors the backend's MAX_FILES_PER_FIELD — how many files a file field can hold. */
export const MAX_FILES_PER_FIELD = 5;

interface SelectOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-permit-details-step',
    templateUrl: './permit-details-step.component.html',
    styleUrls: ['./permit-details-step.component.scss'],
})
export class PermitDetailsStepComponent implements OnInit, OnDestroy {
    @Input() selectedType: PermitTypeConfig | null = null;

    @Input() formSections: FormSectionConfig[] = [];

    @Input() initialFormData: Record<string, any> = {};

    /** originalName per fieldId, carried over so re-entering this step doesn't lose it to the blob URL.
     *  An array, parallel to fileFieldUrls' array. */
    @Input() initialFiles: Record<string, string | string[]> = {};

    @Output() detailsChanged = new EventEmitter<Record<string, any>>();

    /** originalName per fieldId, kept in sync live so leaving via the back arrow doesn't lose it. */
    @Output() filesChanged = new EventEmitter<Record<string, string | string[]>>();

    form: FormGroup | null = null;

    subSteps: SubStep[] = [];

    activeSubStepIndex = 0;

    /** `file` fields upload as soon as they are picked, so formData carries a URL right away. */
    fileFields: FormFieldConfig[] = [];

    /** An array of uploaded urls, up to MAX_FILES_PER_FIELD. */
    fileFieldUrls: Record<string, string | string[]> = {};

    fileFieldNames: Record<string, string | string[]> = {};

    fileFieldUploading: Record<string, boolean> = {};

    dropdownOptions: Record<string, SelectOption[]> = {};

    addressSuggestions: Record<string, Observable<Address[]>> = {};

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private permitService: PermitV2Service,
        private geoidService: GeoidService,
        private messageService: MessageService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.buildForm();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get hasCustomFields(): boolean {
        return this.subSteps.some((s) => s.fields.length > 0);
    }

    fieldLabel(field: FormFieldConfig): string {
        const { label } = field;
        if (!label) return field.fieldId;
        const lang = this.translate.currentLang || 'fr';
        return label[lang] || label.fr || label.en || field.fieldId;
    }

    /** Display unit for a 'number' field (e.g. 'ft²', '$'), or '' if none is set. */
    fieldUnit(field: FormFieldConfig): string {
        const lang = this.translate.currentLang || 'fr';
        return displayUnitFor(field, this.selectedType?.fieldUnits, lang) || '';
    }

    /**
     * Helper subtitle shown under a field label, or '' if none.
     * Suppressed on number fields whose description is already surfaced as a unit (e.g. currency/measurement hints).
     */
    fieldDescription(field: FormFieldConfig): string {
        if (field.fieldType === 'number' && this.fieldUnit(field)) return '';
        const { description } = field;
        if (!description) return '';
        const lang = this.translate.currentLang || 'fr';
        return description[lang] || description.fr || description.en || '';
    }

    // ─── Form construction ───────────────────────────────────────────────────

    private buildForm(): void {
        const all = [...(this.selectedType?.formFields || [])].sort((a, b) => a.displayOrder - b.displayOrder);

        // Required documents first (stable, so displayOrder still orders within a group).
        this.fileFields = all.filter((f) => f.fieldType === 'file').sort((a, b) => Number(b.required) - Number(a.required));
        // 'url' fields are static reference links (city-configured, not citizen-editable) —
        // shown in sections like any other field, but never get a form control.
        const displayFields = all.filter((f) => f.fieldType !== 'file');
        const controlFields = displayFields.filter((f) => f.fieldType !== 'url');

        // Restore anything the agent already uploaded before stepping away. Every file
        // field holds an array (up to MAX_FILES_PER_FIELD) — a legacy single-string
        // value becomes a one-element array.
        this.fileFields.forEach((field) => {
            const existing = this.initialFormData?.[field.fieldId];
            const existingUrls: string[] = Array.isArray(existing) ? existing.slice() : existing ? [existing] : [];
            if (!existingUrls.length) return;
            this.fileFieldUrls[field.fieldId] = existingUrls;
            const names = this.initialFiles?.[field.fieldId];
            const namesArr: string[] = Array.isArray(names) ? names : names ? [names] : [];
            this.fileFieldNames[field.fieldId] = existingUrls.map((url, i) => namesArr[i] ?? this.basename(url));
        });

        // Options first: a restored dropdown value has to be the very option object that the
        // select renders, otherwise coming back to this step shows an empty select.
        controlFields
            .filter((field) => field.fieldType === 'dropdown')
            .forEach((field) => {
                const lang = (this.translate.currentLang || 'fr') as keyof I18nString;
                this.dropdownOptions[field.fieldId] = (field.options || []).map((option) => {
                    if (typeof option === 'string') return { value: option, label: option };

                    const label = typeof option.label === 'string' ? option.label : option.label?.[lang] || option.label?.fr || option.label?.en;
                    return { value: option.value, label: label || option.value };
                });
            });

        const group: Record<string, any> = {};
        controlFields.forEach((field) => {
            group[field.fieldId] = [this.initialValue(field), this.validatorsFor(field)];
        });

        this.form = this.fb.group(group);
        this.buildSubSteps(displayFields);

        controlFields
            .filter((field) => field.fieldType === 'address')
            .forEach((field) => {
                this.addressSuggestions[field.fieldId] = this.form.get(field.fieldId).valueChanges.pipe(
                    startWith(''),
                    filter((value) => typeof value === 'string' && value.trim().length > 3),
                    debounceTime(700),
                    distinctUntilChanged(),
                    switchMap((value: string) => this.geoidService.searchAddressQuery(value)),
                );
            });

        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.emitChange());
    }

    private validatorsFor(field: FormFieldConfig): ValidatorFn[] {
        const validators: ValidatorFn[] = [];
        if (field.required) {
            validators.push(field.fieldType === 'checkbox' ? Validators.requiredTrue : Validators.required);
        }
        if (field.fieldType === 'number') {
            validators.push(Validators.min(field.min != null ? field.min : 0));
            if (field.max != null) validators.push(Validators.max(field.max));
        }
        return validators;
    }

    private initialValue(field: FormFieldConfig): any {
        const stored = this.initialFormData?.[field.fieldId];
        switch (field.fieldType) {
            case 'checkbox':
                return stored === true;
            case 'date':
                return stored ? new Date(stored) : null;
            case 'dropdown': {
                if (!stored) return null;
                return (this.dropdownOptions[field.fieldId] || []).find((option) => option.value === stored) || null;
            }
            default:
                return stored ?? '';
        }
    }

    /** Splits fields into content sub-steps (by formSections, or one fallback group) plus a trailing Documents sub-step for all file fields. */
    private buildSubSteps(displayFields: FormFieldConfig[]): void {
        const contentGroups = groupFieldsBySections(displayFields, this.formSections) ?? (displayFields.length ? [{ title: null, fields: displayFields }] : []);

        this.subSteps = [
            ...contentGroups.map((g) => ({ title: g.title, fields: g.fields, isDocuments: false })),
            ...(this.fileFields.length ? [{ title: null, fields: this.fileFields, isDocuments: true }] : []),
        ];
        this.activeSubStepIndex = 0;
    }

    isOnFirstSubStep(): boolean {
        return this.activeSubStepIndex <= 0;
    }

    isOnLastSubStep(): boolean {
        return this.activeSubStepIndex >= this.subSteps.length - 1;
    }

    goToNextSubStep(): void {
        if (this.isOnLastSubStep()) return;
        this.activeSubStepIndex += 1;
    }

    goToPreviousSubStep(): void {
        if (this.isOnFirstSubStep()) return;
        this.activeSubStepIndex -= 1;
    }

    /** Localized title for a sub-step's pill/heading: the Documents label, a formSection name, or the fallback step title. */
    getSubStepTitle(subStep: SubStep | undefined): string {
        if (!subStep) return '';
        if (subStep.isDocuments) {
            return this.translate.instant('permit.create.details.documents_step');
        }
        if (subStep.title) {
            const lang = this.translate.currentLang || 'fr';
            return subStep.title[lang] || subStep.title.fr || subStep.title.en || '';
        }
        return this.translate.instant('permit.create.details.title');
    }

    /** Whether the currently shown sub-step's own fields are valid — used to gate Suivant while paging through. */
    isCurrentSubStepValid(): boolean {
        const subStep = this.subSteps[this.activeSubStepIndex];
        if (!subStep) return true;
        if (subStep.isDocuments) {
            return !subStep.fields.some((field) => field.required && this.fileFieldCount(field) === 0);
        }
        return subStep.fields.every((field) => {
            const control = this.form?.get(field.fieldId);
            return !control || control.valid;
        });
    }

    /** Reveals field errors for the sub-step the user is stuck on, instead of leaving a disabled Next button unexplained. */
    markCurrentSubStepAsTouched(): void {
        const subStep = this.subSteps[this.activeSubStepIndex];
        subStep?.fields.forEach((field) => this.form?.get(field.fieldId)?.markAsTouched());
    }

    // ─── Address fields ──────────────────────────────────────────────────────

    displayAddressFn = (address: Address | string | null): string => {
        if (!address) return '';
        return typeof address === 'string' ? address : this.formatAddress(address);
    };

    formatAddress(address: Address | null | undefined): string {
        if (!address) return '';
        return [address.unit ? `${address.unit}-` : '', address.number, address.direction, address.streetName || address.street, address.type, address.city, address.province, address.postCode]
            .map((part) => (typeof part === 'string' ? part.trim() : part))
            .filter((part) => !!part)
            .join(' ')
            .replace(/ ,/g, ',')
            .replace(/ {2,}/g, ' ')
            .replace(/^ | $/g, '')
            .replace(/,(?=\S)/g, ', ');
    }

    onAddressPicked(field: FormFieldConfig, address: Address): void {
        this.form.get(field.fieldId).setValue(this.formatAddress(address));
    }

    // ─── File-type fields ────────────────────────────────────────────────────

    private basename(url: string): string {
        return url.split('?')[0].split('/').pop() || url;
    }

    /** Every file field holds an array, up to MAX_FILES_PER_FIELD. */
    fileFieldCount(field: FormFieldConfig): number {
        const value = this.fileFieldUrls[field.fieldId];
        return Array.isArray(value) ? value.length : value ? 1 : 0;
    }

    isFileFieldFull(field: FormFieldConfig): boolean {
        return this.fileFieldCount(field) >= MAX_FILES_PER_FIELD;
    }

    /** A multi-file field's uploaded urls, for `*ngFor` in the template. */
    fileUrlsFor(field: FormFieldConfig): string[] {
        const value = this.fileFieldUrls[field.fieldId];
        return Array.isArray(value) ? value : [];
    }

    fileNameAt(field: FormFieldConfig, index: number): string {
        const names = this.fileFieldNames[field.fieldId];
        return (Array.isArray(names) ? names[index] : undefined) || this.basename(this.fileUrlsFor(field)[index] || '');
    }

    onFieldFileSelected(field: FormFieldConfig, event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input?.files?.length) return;
        const file = input.files[0];
        input.value = '';

        if (this.isFileFieldFull(field)) {
            this.messageService.error(this.translate.instant('permit.create.errors.max_files_reached', { max: MAX_FILES_PER_FIELD }));
            return;
        }

        this.fileFieldUploading[field.fieldId] = true;
        this.permitService
            .uploadTempFile(file)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (urls: string[]) => {
                    const url = Array.isArray(urls) ? urls[0] : (urls as any);
                    const currentUrls: string[] = Array.isArray(this.fileFieldUrls[field.fieldId]) ? (this.fileFieldUrls[field.fieldId] as string[]) : [];
                    const currentNames: string[] = Array.isArray(this.fileFieldNames[field.fieldId]) ? (this.fileFieldNames[field.fieldId] as string[]) : [];
                    this.fileFieldUrls[field.fieldId] = [...currentUrls, url];
                    this.fileFieldNames[field.fieldId] = [...currentNames, file.name];
                    this.fileFieldUploading[field.fieldId] = false;
                    this.emitChange();
                },
                error: () => {
                    this.fileFieldUploading[field.fieldId] = false;
                    this.messageService.error(this.translate.instant('permit.create.errors.upload'));
                },
            });
    }

    removeFieldFile(field: FormFieldConfig, index: number): void {
        const urls: string[] = Array.isArray(this.fileFieldUrls[field.fieldId]) ? (this.fileFieldUrls[field.fieldId] as string[]) : [];
        const names: string[] = Array.isArray(this.fileFieldNames[field.fieldId]) ? (this.fileFieldNames[field.fieldId] as string[]) : [];
        this.fileFieldUrls[field.fieldId] = urls.filter((_, i) => i !== index);
        this.fileFieldNames[field.fieldId] = names.filter((_, i) => i !== index);
        this.emitChange();
    }

    // ─── Validity / collection ───────────────────────────────────────────────

    isValid(): boolean {
        const missingFile = this.fileFields.some((field) => field.required && this.fileFieldCount(field) === 0);
        if (missingFile) return false;
        return !this.form || this.form.valid;
    }

    markAllAsTouched(): void {
        this.form?.markAllAsTouched();
    }

    collect(): Record<string, any> {
        const raw = this.form ? this.form.getRawValue() : {};
        const formData: Record<string, any> = {};

        this.subSteps
            .filter((s) => !s.isDocuments)
            .forEach((section) =>
                section.fields.forEach((field) => {
                    const value = this.serialize(field, raw[field.fieldId]);
                    if (value !== null && value !== undefined && value !== '') {
                        formData[field.fieldId] = value;
                    } else if (field.fieldType === 'checkbox') {
                        formData[field.fieldId] = false;
                    }
                }),
            );

        this.fileFields.forEach((field) => {
            if (this.fileFieldCount(field) > 0) {
                formData[field.fieldId] = this.fileFieldUrls[field.fieldId];
            }
        });

        return formData;
    }

    private serialize(field: FormFieldConfig, value: any): any {
        switch (field.fieldType) {
            case 'number':
                return value === '' || value == null ? null : Number(value);
            case 'date':
                return value instanceof Date ? value.toISOString() : value || null;
            case 'dropdown':
                // app-form-select-v2 stores the whole option object; the API wants the scalar.
                return value && typeof value === 'object' ? value.value : value;
            case 'checkbox':
                return !!value;
            default:
                return typeof value === 'string' ? value.trim() : value;
        }
    }

    private emitChange(): void {
        this.detailsChanged.emit(this.collect());
        this.filesChanged.emit({ ...this.fileFieldNames });
    }
}
